import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, MessageCircle, HelpCircle, Lightbulb, AlertTriangle, Send } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';
import Navigation from '../components/Navigation';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TIPOS = [
  { value: 'ajuda', label: 'Ajuda', icon: HelpCircle, color: 'bg-blue-500', desc: 'Preciso de ajuda com algo' },
  { value: 'sugestao', label: 'Sugestao', icon: Lightbulb, color: 'bg-amber-500', desc: 'Tenho uma ideia ou sugestao' },
  { value: 'erro', label: 'Erro', icon: AlertTriangle, color: 'bg-red-500', desc: 'Encontrei um problema ou erro' },
  { value: 'outro', label: 'Outro', icon: MessageCircle, color: 'bg-gray-500', desc: 'Outro assunto' },
];

const FaleConosco = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [whatsappUrl, setWhatsappUrl] = useState('');
  const [formData, setFormData] = useState({
    tipo: '',
    mensagem: '',
    nome: '',
    contato: '',
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.tipo) { toast.error('Selecione o tipo da mensagem'); return; }
    if (!formData.mensagem.trim()) { toast.error('Escreva sua mensagem'); return; }

    setLoading(true);
    try {
      const res = await axios.post(`${API}/feedback`, formData);
      setWhatsappUrl(res.data.whatsapp_url || '');
      setSent(true);
      toast.success('Mensagem enviada com sucesso!');
    } catch {
      toast.error('Erro ao enviar mensagem');
    } finally { setLoading(false); }
  };

  if (sent) {
    return (
      <div className="min-h-screen bg-[#F9F9F7] pb-24">
        <div className="bg-[#1A4D2E] text-white px-6 py-6">
          <div className="max-w-3xl mx-auto">
            <button data-testid="back-button" onClick={() => navigate(-1)} className="mb-4 hover:bg-white/10 rounded-full p-2 transition-all">
              <ArrowLeft className="w-6 h-6" />
            </button>
            <div className="flex items-center gap-3">
              <MessageCircle className="w-7 h-7" strokeWidth={2.5} />
              <h1 className="font-nunito font-bold text-3xl">Mensagem Enviada</h1>
            </div>
          </div>
        </div>
        <div className="max-w-3xl mx-auto px-4 py-12">
          <Card className="bg-white rounded-2xl p-8 shadow-lg border border-[#E1ECE5] text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Send className="w-8 h-8 text-green-600" />
            </div>
            <h2 className="font-nunito font-bold text-2xl text-[#1A4D2E] mb-2">Obrigado!</h2>
            <p className="font-inter text-gray-600 mb-6">Sua mensagem foi registrada. O desenvolvedor sera notificado.</p>
            {whatsappUrl && (
              <a
                href={whatsappUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="whatsapp-link"
                className="inline-flex items-center gap-2 bg-green-500 hover:bg-green-600 text-white font-semibold rounded-full px-6 py-3 transition-all mb-4"
              >
                <MessageCircle className="w-5 h-5" />
                Enviar tambem via WhatsApp
              </a>
            )}
            <div className="mt-4">
              <Button onClick={() => { setSent(false); setFormData({ tipo: '', mensagem: '', nome: '', contato: '' }); }} variant="outline" className="rounded-full" data-testid="new-message-btn">
                Enviar outra mensagem
              </Button>
            </div>
          </Card>
        </div>
        <Navigation />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9F9F7] pb-24">
      <div className="bg-[#1A4D2E] text-white px-6 py-6">
        <div className="max-w-3xl mx-auto">
          <button data-testid="back-button" onClick={() => navigate(-1)} className="mb-4 hover:bg-white/10 rounded-full p-2 transition-all">
            <ArrowLeft className="w-6 h-6" />
          </button>
          <div className="flex items-center gap-3">
            <MessageCircle className="w-7 h-7" strokeWidth={2.5} />
            <h1 className="font-nunito font-bold text-3xl">Fale Conosco</h1>
          </div>
          <p className="font-inter text-white/70 text-sm mt-2">Ajuda, sugestoes ou indicacao de erros</p>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-8">
        <Card className="bg-white rounded-2xl p-6 md:p-8 shadow-lg border border-[#E1ECE5]">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Tipo */}
            <div>
              <Label className="font-inter font-semibold text-[#1A4D2E] mb-3 block">Qual o motivo do contato? *</Label>
              <div className="grid grid-cols-2 gap-3">
                {TIPOS.map((t) => (
                  <button
                    key={t.value}
                    type="button"
                    data-testid={`tipo-${t.value}`}
                    onClick={() => setFormData((p) => ({ ...p, tipo: t.value }))}
                    className={`text-left p-4 rounded-xl border-2 transition-all ${
                      formData.tipo === t.value
                        ? 'border-[#1A4D2E] bg-[#F2F7F4]'
                        : 'border-gray-100 hover:border-gray-300'
                    }`}
                  >
                    <div className={`w-8 h-8 ${t.color} rounded-lg flex items-center justify-center mb-2`}>
                      <t.icon className="w-4 h-4 text-white" />
                    </div>
                    <p className="font-inter font-semibold text-sm text-[#1A4D2E]">{t.label}</p>
                    <p className="font-inter text-xs text-gray-400 mt-0.5">{t.desc}</p>
                  </button>
                ))}
              </div>
            </div>

            {/* Nome */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="font-inter font-semibold text-[#1A4D2E] mb-2 block">Seu nome</Label>
                <Input data-testid="input-nome" value={formData.nome} onChange={(e) => setFormData((p) => ({ ...p, nome: e.target.value }))} className="rounded-xl border-gray-200 bg-gray-50/50" placeholder="Opcional" />
              </div>
              <div>
                <Label className="font-inter font-semibold text-[#1A4D2E] mb-2 block">Contato (celular/email)</Label>
                <Input data-testid="input-contato" value={formData.contato} onChange={(e) => setFormData((p) => ({ ...p, contato: e.target.value }))} className="rounded-xl border-gray-200 bg-gray-50/50" placeholder="Opcional" />
              </div>
            </div>

            {/* Mensagem */}
            <div>
              <Label className="font-inter font-semibold text-[#1A4D2E] mb-2 block">Mensagem *</Label>
              <textarea
                data-testid="input-mensagem"
                value={formData.mensagem}
                onChange={(e) => setFormData((p) => ({ ...p, mensagem: e.target.value }))}
                className="w-full rounded-xl border border-gray-200 bg-gray-50/50 p-3 text-sm min-h-[120px] focus:outline-none focus:ring-2 focus:ring-[#1A4D2E]/20 focus:border-[#1A4D2E]"
                placeholder="Descreva sua duvida, sugestao ou o erro encontrado..."
                required
              />
            </div>

            <Button type="submit" data-testid="submit-feedback" disabled={loading} className="w-full bg-[#1A4D2E] hover:bg-[#145A32] text-white rounded-full py-6 text-lg font-semibold shadow-md active:scale-95 transition-all">
              {loading ? 'Enviando...' : 'Enviar Mensagem'}
            </Button>
          </form>
        </Card>
      </div>
      <Navigation />
    </div>
  );
};

export default FaleConosco;
